import React from "react";
import { Terminal, Play, Cpu, Clock, Zap, ShieldCheck, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
const labs = [{
  id: "lab-01",
  title: "Core Web Vitals: LCP Optimization",
  difficulty: "Beginner",
  prerequisite: "Chrome DevTools",
  duration: "15 min",
  status: "Available"
}, {
  id: "lab-02",
  title: "Advanced Image Formats (AVIF/WebP)",
  difficulty: "Intermediate",
  prerequisite: "ImageMagick",
  duration: "25 min",
  status: "Available"
}, {
  id: "lab-03",
  title: "React Hydration Strategies",
  difficulty: "Advanced",
  prerequisite: "React 18+",
  duration: "45 min",
  status: "Locked"
}, {
  id: "lab-04",
  title: "Server-Side Caching with Redis",
  difficulty: "Expert",
  prerequisite: "Redis 6+",
  duration: "60 min",
  status: "Locked"
}, {
  id: "lab-05",
  title: "Third-Party Script Auditing",
  difficulty: "Intermediate",
  prerequisite: "JS Profiling",
  duration: "30 min",
  status: "Available"
}];
const getDifficultyColor = (level: string) => {
  switch (level) {
    case "Beginner":
      return "bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 border-emerald-500/20";
    case "Intermediate":
      return "bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 border-amber-500/20";
    case "Advanced":
      return "bg-orange-500/10 text-orange-500 hover:bg-orange-500/20 border-orange-500/20";
    case "Expert":
      return "bg-red-500/10 text-red-500 hover:bg-red-500/20 border-red-500/20";
    default:
      return "bg-secondary text-secondary-foreground";
  }
};
export default function TheSpeedLabPreview() {
  return <section id="the-speed-lab-preview" className="dark bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />

      <div className="max-w-5xl mx-auto relative z-10">
        <div className="mb-12 text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-mono font-medium border border-primary/20">
            <Terminal className="size-3" />
            <span>INTERACTIVE MODULES</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            The Speed Lab
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Hands-on performance engineering challenges. Test your skills in a
            simulated production environment.
          </p>
        </div>

        {}
        <Card className="bg-[#0c0c0c] border-border/20 shadow-2xl overflow-hidden ring-1 ring-white/5">
          {}
          <CardHeader className="bg-[#1a1a1a] px-4 py-3 border-b border-white/5 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="size-3 rounded-full bg-[#ff5f56] border border-[#e0443e]" />
                <div className="size-3 rounded-full bg-[#ffbd2e] border border-[#dea123]" />
                <div className="size-3 rounded-full bg-[#27c93f] border border-[#1aab29]" />
              </div>
            </div>
            <div className="absolute left-1/2 -translate-x-1/2 text-xs font-mono text-muted-foreground/60 flex items-center gap-2 select-none">
              <Terminal className="size-3" />
              <span>guest@webperf-news:~/labs</span>
            </div>
            <div className="text-xs text-muted-foreground/40 font-mono hidden sm:block">
              bash — 80x24
            </div>
          </CardHeader>

          {}
          <CardContent className="p-0 font-mono text-sm">
            {}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 text-xs font-medium text-muted-foreground/60 border-b border-white/5 bg-white/[0.02] uppercase tracking-wider">
              <div className="col-span-5 pl-2">Challenge</div>
              <div className="col-span-2">Difficulty</div>
              <div className="col-span-3">Prerequisite</div>
              <div className="col-span-2 text-right pr-2">Action</div>
            </div>

            {}
            <div className="divide-y divide-white/5">
              {labs.map((lab, i) => <div key={lab.id} data-index={i} className="group relative grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 md:py-3 items-center hover:bg-white/[0.03] transition-colors duration-200">
                  {}
                  <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                  {}
                  <div className="col-span-1 md:col-span-5 flex items-center gap-3">
                    <div className="text-muted-foreground/40 group-hover:text-primary transition-colors">
                      <ChevronRight className="size-4" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground group-hover:text-primary transition-colors">
                        {lab.title}
                      </div>
                      <div className="md:hidden flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <Clock className="size-3" />
                        {lab.duration}
                      </div>
                    </div>
                  </div>

                  {}
                  <div className="col-span-1 md:col-span-2 flex items-center">
                    <Badge variant="outline" className={`rounded-sm px-2 py-0.5 text-[10px] uppercase tracking-wider border ${getDifficultyColor(lab.difficulty)}`}>
                      {lab.difficulty}
                    </Badge>
                  </div>

                  {}
                  <div className="col-span-1 md:col-span-3 flex items-center gap-2 text-muted-foreground text-xs md:text-sm">
                    <Cpu className="size-3.5 md:hidden" />
                    <span className="hidden md:inline text-muted-foreground/40 mr-2">
                      REQ:
                    </span>
                    {lab.prerequisite}
                  </div>

                  {}
                  <div className="col-span-1 md:col-span-2 flex items-center justify-start md:justify-end">
                    {lab.status === "Available" ? <Button asChild size="sm" variant="ghost" className="h-8 text-xs font-mono hover:bg-primary hover:text-primary-foreground w-full md:w-auto justify-start md:justify-center">
                        <Link to={`/labs/${lab.id}`}>
                          <Play className="size-3 mr-2" />
                          Start Lab
                        </Link>
                      </Button> : <Button disabled size="sm" variant="ghost" className="h-8 text-xs font-mono opacity-50 w-full md:w-auto justify-start md:justify-center cursor-not-allowed">
                        <ShieldCheck className="size-3 mr-2" />
                        Locked
                      </Button>}
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Button asChild variant="link" className="text-muted-foreground hover:text-primary">
            <Link to="/GuidesTutorialsPage">
              View all 24 available labs <span aria-hidden="true">&rarr;</span>
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}